import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { X, ShoppingBag, CreditCard, Info, Package as PackageIcon, Truck } from "lucide-react";
import { formatAmount } from "@/lib/currency";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api from "@/lib/api";
import { toast } from "sonner";
import { tierPrice, PACKAGE_LABEL } from "@/lib/packages";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const Cart = () => {
  const { cart, removeFromCart, currency, user, setCurrency, clearCart } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Auto-pick a sensible default per currency
  const [method, setMethod] = useState(currency === "inr" ? "razorpay" : "stripe");

  const priceField = currency === "inr" ? "price_inr" : "price_usd"; // eslint-disable-line no-unused-vars
  // Iter48 — Per-line price uses the SELECTED tier's price (not plan.price_*)
  // so the total matches what the server will compute at checkout.
  const lineAmount = (p, curr) => tierPrice(p, p.package_tier || "pdf", curr);
  const total = cart.reduce((sum, p) => sum + Number(lineAmount(p, currency) || 0), 0);
  const totalUsd = cart.reduce((sum, p) => sum + Number(lineAmount(p, "usd") || 0), 0);
  const totalInr = cart.reduce((sum, p) => sum + Number(lineAmount(p, "inr") || 0), 0);
  // Iter60A — fp_cad / fp_revit also require manual delivery like their premium counterparts.
  const hasManualDelivery = cart.some((p) => {
    const t = p.package_tier || "pdf";
    return t === "cad" || t === "revit" || t === "fp_cad" || t === "fp_revit";
  });
  const cartItems = cart.map((p) => ({ plan_id: p.id, package_tier: p.package_tier || "pdf" }));

  const handleStripeCheckout = async () => {
    if (!user) { toast.info("Please sign in to checkout"); navigate("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: cartItems,
        currency,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Checkout failed", { description: e.response?.data?.detail || "Try again." });
      setLoading(false);
    }
  };

  const handleRazorpayCheckout = async () => {
    if (!user) { toast.info("Please sign in to checkout"); navigate("/login"); return; }
    setLoading(true);
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error("Failed to load Razorpay");
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.post("/razorpay/create-order", {
        items: cartItems,
      });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Dream Home",
        description: `${cart.length} architectural plan(s)`,
        order_id: data.order_id,
        prefill: {
          name: user.name,
          email: user.email,
        },
        theme: { color: "#2C4C3B" },
        handler: async (response) => {
          try {
            const verify = await api.post("/razorpay/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verify.data.payment_status === "paid") {
              clearCart();
              toast.success("Payment complete");
              navigate(`/checkout/success?razorpay=1&order_id=${response.razorpay_order_id}`);
            } else {
              toast.error("Payment verification failed");
            }
          } catch (e) {
            toast.error("Verification failed", { description: e.response?.data?.detail || "Contact support." });
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error("Payment failed", { description: response.error?.description || "Try again." });
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error("Checkout failed", { description: e.response?.data?.detail || "Try again." });
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 lg:py-32 text-center">
          <ShoppingBag size={40} className="mx-auto text-accent" />
          <div className="overline text-accent mt-6">Empty basket</div>
          <h1 className="font-display text-5xl mt-3">No plans yet.</h1>
          <p className="mt-4 text-muted-foreground">Add a house plan to your cart from the collection.</p>
          <Link to="/plans">
            <Button className="rounded-none bg-primary mt-10 h-12 px-8" data-testid="empty-cart-browse">Browse plans</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const paypalAvailable = currency === "usd"; // USD only
  const razorpayAvailable = currency === "inr"; // INR only

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
        <div className="overline text-accent">Basket</div>
        <h1 className="font-display text-5xl lg:text-6xl mt-3 mb-12 leading-none tracking-tighter">Review your plans</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8" data-testid="cart-items">
            {cart.map((plan) => {
              const tier = plan.package_tier || "pdf";
              const linePrice = lineAmount(plan, currency);
              return (
              <div key={plan.id} className="flex gap-6 py-6 border-b border-border" data-testid={`cart-item-${plan.id}`}>
                <div className="w-32 h-24 shrink-0 overflow-hidden bg-muted">
                  <img src={plan.images?.[0]} alt={plan.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline text-accent">{plan.style}</div>
                  <div className="font-display text-2xl mt-1">{plan.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {plan.bedrooms} bd · {plan.bathrooms} ba · {plan.sqft.toLocaleString()} sqft
                  </div>
                  <div className="mt-2 inline-flex items-center gap-2 border border-primary/40 bg-primary/5 px-2 py-1" data-testid={`cart-item-tier-${plan.id}`}>
                    <PackageIcon size={11} className="text-primary" />
                    <span className="text-[10px] uppercase tracking-widest text-primary font-medium">
                      {PACKAGE_LABEL[tier]} Package
                    </span>
                    {tier !== "pdf" && (
                      <span className="text-[10px] text-accent inline-flex items-center gap-1">
                        · <Truck size={10} /> Manual delivery
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl text-primary" data-testid={`cart-item-price-${plan.id}`}>{formatAmount(linePrice, currency)}</div>
                  <button
                    onClick={() => removeFromCart(plan.id)}
                    data-testid={`remove-${plan.id}`}
                    className="text-xs text-muted-foreground hover:text-destructive mt-2 flex items-center gap-1 ml-auto"
                  >
                    <X size={12} /> Remove
                  </button>
                </div>
              </div>
              );
            })}
          </div>

          <div className="lg:col-span-4">
            <div className="border border-border p-8 sticky top-28">
              <div className="overline text-accent">Order summary</div>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Items</span><span>{cart.length}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Currency</span><span className="uppercase font-semibold">{currency}</span></div>
              </div>
              <div className="mt-6 pt-6 border-t border-border flex justify-between items-baseline">
                <span className="overline">Total</span>
                <span data-testid="cart-total" className="font-display text-3xl text-primary">{formatAmount(total, currency)}</span>
              </div>

              {hasManualDelivery && (
                <div className="mt-4 border border-accent/40 bg-accent/5 p-3 flex items-start gap-2" data-testid="manual-delivery-notice">
                  <Truck size={14} className="text-accent shrink-0 mt-0.5" />
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    <span className="font-medium text-foreground">Manual delivery included.</span> Your CAD/Revit
                    files will be reviewed and emailed by our team within our committed timeframe after payment is confirmed.
                  </div>
                </div>
              )}

              {/* Currency switcher hint */}
              <div className="mt-3 text-xs text-muted-foreground text-right">
                {currency === "usd" ? (
                  <button className="underline hover:text-accent" onClick={() => { setCurrency("inr"); setMethod("razorpay"); }} data-testid="switch-inr">
                    Pay in INR ({formatAmount(totalInr, "inr")}) →
                  </button>
                ) : (
                  <button className="underline hover:text-accent" onClick={() => { setCurrency("usd"); setMethod("stripe"); }} data-testid="switch-usd">
                    Pay in USD ({formatAmount(totalUsd, "usd")}) →
                  </button>
                )}
              </div>

              {/* Payment method selector */}
              <div className="mt-6">
                <div className="overline text-muted-foreground mb-3">Payment method</div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    data-testid="method-stripe"
                    onClick={() => setMethod("stripe")}
                    className={`border px-2 py-3 text-xs font-medium transition-fast ${method === "stripe" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground"}`}
                  >
                    <CreditCard size={12} className="inline mr-1" /> Stripe
                  </button>
                  <button
                    data-testid="method-paypal"
                    onClick={() => paypalAvailable && setMethod("paypal")}
                    disabled={!paypalAvailable}
                    className={`border px-2 py-3 text-xs font-medium transition-fast ${method === "paypal" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground"} ${!paypalAvailable ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    PayPal
                  </button>
                  <button
                    data-testid="method-razorpay"
                    onClick={() => razorpayAvailable && setMethod("razorpay")}
                    disabled={!razorpayAvailable}
                    className={`border px-2 py-3 text-xs font-medium transition-fast ${method === "razorpay" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-foreground"} ${!razorpayAvailable ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    Razorpay
                  </button>
                </div>
                {!paypalAvailable && method === "paypal" && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
                    <Info size={14} className="shrink-0 mt-0.5" />
                    <span>PayPal supports USD only. Switch currency above.</span>
                  </div>
                )}
                {!razorpayAvailable && method === "razorpay" && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
                    <Info size={14} className="shrink-0 mt-0.5" />
                    <span>Razorpay supports INR only. Switch currency above.</span>
                  </div>
                )}
              </div>

              {/* Stripe checkout */}
              {method === "stripe" && (
                <>
                  <Button
                    onClick={handleStripeCheckout}
                    disabled={loading}
                    data-testid="checkout-btn"
                    className="w-full rounded-none bg-accent hover:bg-accent/90 h-14 mt-6"
                  >
                    {loading ? "Loading…" : "Checkout with Stripe"}
                  </Button>
                  <p className="mt-4 text-xs text-muted-foreground text-center">
                    Cards · UPI · Wallets · Secure via Stripe
                  </p>
                </>
              )}

              {/* Razorpay checkout */}
              {method === "razorpay" && razorpayAvailable && RAZORPAY_KEY_ID && (
                <>
                  <Button
                    onClick={handleRazorpayCheckout}
                    disabled={loading}
                    data-testid="razorpay-checkout-btn"
                    className="w-full rounded-none bg-accent hover:bg-accent/90 h-14 mt-6"
                  >
                    {loading ? "Opening…" : "Pay with Razorpay"}
                  </Button>
                  <p className="mt-4 text-xs text-muted-foreground text-center">
                    UPI · Cards · Netbanking · Wallets · INR only
                  </p>
                </>
              )}

              {/* PayPal checkout */}
              {method === "paypal" && paypalAvailable && PAYPAL_CLIENT_ID && (
                <div className="mt-6" data-testid="paypal-buttons-container">
                  {!user && (
                    <p className="mb-3 text-xs text-destructive">Please sign in first.</p>
                  )}
                  <PayPalScriptProvider
                    options={{
                      clientId: PAYPAL_CLIENT_ID,
                      currency: "USD",
                      intent: "capture",
                      "disable-funding": "credit,card",
                    }}
                  >
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                      disabled={!user || loading}
                      forceReRender={[totalUsd, cart.length, user?.user_id]}
                      createOrder={async () => {
                        try {
                          const { data } = await api.post("/paypal/create-order", { items: cartItems });
                          return data.order_id;
                        } catch (e) {
                          toast.error("PayPal order failed", { description: e.response?.data?.detail || "Try again." });
                          throw e;
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          const res = await api.post(`/paypal/capture-order/${data.orderID}`);
                          if (res.data.payment_status === "paid") {
                            clearCart();
                            toast.success("Payment complete");
                            navigate(`/checkout/success?paypal=1&order_id=${data.orderID}`);
                          } else {
                            toast.error("Payment not completed", { description: res.data.payment_status });
                          }
                        } catch (e) {
                          toast.error("Capture failed", { description: e.response?.data?.detail || "Try again." });
                        }
                      }}
                      onError={(err) => {
                        console.error(err);
                        toast.error("PayPal error", { description: "Please try again." });
                      }}
                    />
                  </PayPalScriptProvider>
                  <p className="mt-3 text-xs text-muted-foreground text-center">
                    PayPal · USD only
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Cart;
